import * as React from 'react';
import { LeafletGeoJSON, LeafletMap, LeafletComponent } from '../LeafletMap';
import { StyledLegendBox, StyledLegendRow, StyledLegendColor, StyledLegendText } from './ChartMap.style';
import geoJSON from './geo.json';
import { IFeature, IGeoJSON } from './types';
import { autoFocus } from './utils/autoFocus';
import { highlight } from './utils/highlight';
import L from 'leaflet';
import { MapDataRange, RangeType } from '../../models/MapDataRange';

type ChartMapEventType = 'layer-over' | 'layer-out' | 'legend-over' | 'legend-out' | 'legend-click';
const timers = {};

export const ChartMap = (props: any) => {
  const { options, geoOptions, geoPipeline, ranges, children, hoverColor, useAutoFocus = true, fitWorld = true, onMount = () => { } } = props;
  const dataRanges: MapDataRange[] = ranges.filter(range => range.type !== RangeType.NoData && range.from !== undefined && range.to !== undefined);
  const [selectedColors, setSelectedColors] = React.useState<string[]>([]);
  const [hoveredColor, setHoveredColor] = React.useState<string | undefined>();
  const [leafletMap, setLeafletMap] = React.useState<L.Map | undefined>();

  React.useEffect(() => {
    clearTimeout(timers['highlight']);
    timers['highlight'] = setTimeout(() => leafletMap && highlight(leafletMap, hoveredColor ? [hoveredColor] : selectedColors), 100);
  }, [leafletMap, hoveredColor, selectedColors]);

  const handlerEvents = React.useCallback(function (this: any, eventType: ChartMapEventType, ...args: unknown[]): any {
    switch (eventType) {
      case 'layer-out':
      case 'layer-over': {
        return function (this: L.Layer & any) {
          const [feature] = args as [IFeature];
          const style = this.defaultOptions.style(feature);
          if (eventType === "layer-over") {
            this.setStyle({ weight: style.weight + 2, color: hoverColor });
          } else {
            this.setStyle({ weight: style.weight, color: style.color });
          }
        };
      }
      case 'legend-out':
      case 'legend-over': {
        const [range] = args as [MapDataRange];
        return () => {
          clearTimeout(timers['legend']);
          timers['legent'] = setTimeout(() => setHoveredColor(eventType === 'legend-over' ? range.color : undefined), 100);
        }
      }
      case 'legend-click': {
        const [range] = args as [MapDataRange];
        return () => {
          const colors = selectedColors.includes(range.color) ? selectedColors.filter(e => e !== range.color) : [...selectedColors, range.color];
          setSelectedColors(colors);
        }
      }
      default:
        throw new Error('Event handler not implemented');
    }
  }, [hoverColor, selectedColors]);

  const geoJSONOptions = React.useMemo(() => ({
    ...geoOptions, onEachFeature: (feature: IFeature, layer: L.Layer & any) => {
      if (hoverColor) {
        layer.on('mouseover', handlerEvents('layer-over', feature));
        layer.on('mouseout', handlerEvents('layer-out', feature));
      }
    }
  }), [geoOptions, handlerEvents, hoveredColor]);

  const onMountGeoLayer = React.useCallback((map: L.Map, geojson: IGeoJSON) => {
    if (useAutoFocus) {
      autoFocus(map, geojson, (feature) => {
        return Boolean(feature.properties['hasData']);
      });
    } else if (fitWorld) {
      map.fitWorld();
    }
    window['lmap'] = map;
    clearTimeout(timers['highlight']);
    highlight(map, hoveredColor ? [hoveredColor] : selectedColors);
    setLeafletMap(map);
    onMount(map);
  }, [hoveredColor, selectedColors, useAutoFocus, fitWorld]);

  const legend = React.useMemo(() => {
    return (<StyledLegendBox data-selected={selectedColors.length || undefined}>
      {dataRanges.filter(Boolean).map((range, i) => {
        const nextRange = dataRanges[i + 1];
        const prevRange = dataRanges[i - 1];
        const displayTo = nextRange ? nextRange.displayFrom : range.displayTo;
        let displayText = '';
        switch (range.type) {
          case RangeType.First:
            displayText = `less than ${displayTo}`;
            break;
          case RangeType.Middle:
            displayText = `from ${range.displayFrom} to ${nextRange?.type === RangeType.Last ? range.displayTo : displayTo}`;
            break;
          case RangeType.Last:
            displayText = `more than ${prevRange ? prevRange.displayTo : range.displayFrom}`;
            break;
          default:
            break;
        }
        return (
          <StyledLegendRow
            key={`${range.from}-${range.to}`}
            data-selected={selectedColors.includes(range.color) || undefined}
            onPointerOver={handlerEvents('legend-over', range)}
            onPointerOut={handlerEvents('legend-out', range)}
            onClick={handlerEvents('legend-click', range)}
          >
            <StyledLegendColor color={range.color} />
            <StyledLegendText>{displayText}</StyledLegendText>
          </StyledLegendRow>
        )
      })}
      {ranges.filter(range => range.type === RangeType.NoData).map((range, i) => (
        <StyledLegendRow key="no-data"
          data-selected={selectedColors.includes(range.color) || undefined}
          onPointerOver={handlerEvents('legend-over', range)}
          onPointerOut={handlerEvents('legend-out', range)}
          onClick={handlerEvents('legend-click', range)}
        >
          <StyledLegendColor color={range.color} />
          <StyledLegendText>no data</StyledLegendText>
        </StyledLegendRow>
      ))}
      <StyledLegendRow
        data-selected={selectedColors.includes("rgb(159, 159, 159)") || undefined}
        onPointerOver={handlerEvents('legend-over', { color: "rgb(159, 159, 159)" })}
        onPointerOut={handlerEvents('legend-out', { color: "rgb(159, 159, 159)" })}
        onClick={handlerEvents('legend-click', { color: "rgb(159, 159, 159)" })}
      >
        <StyledLegendColor color="rgb(159, 159, 159)" />
        <StyledLegendText>not in source</StyledLegendText>
      </StyledLegendRow>
    </StyledLegendBox>);
  }, [handlerEvents, ranges, selectedColors]);

  return <LeafletMap options={options}>
    <LeafletGeoJSON geoJSON={geoJSON} options={geoJSONOptions} pipeline={geoPipeline} onMount={onMountGeoLayer}>{children}</LeafletGeoJSON>
    <LeafletComponent id="legend" options={{ position: 'bottomleft' }}>
      {legend}
    </LeafletComponent>
  </LeafletMap>;
}
